import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { login, loginGoogle, user } = useAuth();
  const nav = useNavigate();
  const { t } = useTranslation();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [needsVerify, setNeedsVerify] = useState(false);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    // Handle Emergent Google redirect
    const hash = window.location.hash;
    if (hash.includes("session_id=")) {
      const sid = new URLSearchParams(hash.substring(1)).get("session_id");
      if (sid) {
        setBusy(true);
        loginGoogle(sid).then(() => {
          window.location.hash = "";
          toast.success(t("auth.login_google_success"));
          nav("/dashboard");
        }).catch(() => {
          toast.error(t("auth.login_google_failed"));
          setBusy(false);
        });
      }
    }
  }, [loginGoogle, nav]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (user) nav("/dashboard"); }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setNeedsVerify(false);
    try {
      await login(email, password);
      toast.success(t("auth.login_welcome_toast"));
      nav("/dashboard");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const code = err?.code;
      // Backend returns 403 with {code: "email_not_verified", message: "..."}
      // for unverified users. Show the resend UI instead of an error toast.
      if (status === 403 && detail && typeof detail === "object" && detail.code === "email_not_verified") {
        setNeedsVerify(true);
        setBusy(false);
        return;
      }
      let msg;
      if (status === 401 || status === 403) {
        msg = typeof detail === "string" && detail.length > 0 ? detail : t("auth.login_invalid_credentials");
      } else if (typeof detail === "string" && detail.length > 0) {
        msg = detail;
      } else if (code === "ECONNABORTED" || /timeout/i.test(err?.message || "")) {
        msg = t("auth.login_timeout");
      } else if (!err?.response) {
        msg = t("auth.login_unavailable");
      } else if (status >= 500) {
        msg = t("auth.login_server_error");
      } else {
        msg = t("auth.login_generic_error");
      }
      toast.error(msg);
    }
    setBusy(false);
  };

  const google = () => {
    const redirect = `${window.location.origin}/login`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-semibold tracking-tight mb-2">{t("auth.login_title")}</h1>
          <p className="text-sm text-zinc-500">{t("landing.hero_subtitle")}</p>
        </div>
        <div className="bg-[#12141C] border border-[#1E212B] rounded-xl p-8">
          <Button
            onClick={google}
            data-testid="google-login-btn"
            variant="outline"
            className="w-full mb-4 bg-[#0F1119] border-[#1E212B] hover:bg-[#161821] hover:border-[#2D313F] text-white h-11"
            disabled={busy}
          >
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            {t("auth.login_google_continue")}
          </Button>
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[#1E212B]" /></div>
            <span className="relative flex justify-center text-xs text-zinc-500 bg-[#12141C] px-2 font-mono">{t("auth.login_or")}</span>
          </div>
          <form onSubmit={submit} className="space-y-4">
            {needsVerify && (
              <div
                className="border border-amber-500/40 bg-amber-500/10 text-amber-300 rounded-lg px-3 py-3 text-sm"
                data-testid="login-verify-required"
              >
                <div className="mb-2">
                  {t("auth.login_needs_verify_msg")}
                </div>
                <Button
                  type="button"
                  disabled={resending}
                  data-testid="login-resend-verify-btn"
                  onClick={async () => {
                    setResending(true);
                    try {
                      await api.post("/auth/resend-verification", { email });
                      toast.success(t("auth.signup_resend_toast_ok"));
                    } catch (_) {
                      toast.error(t("auth.signup_resend_toast_err"));
                    }
                    setResending(false);
                  }}
                  className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-500/40 h-8 text-xs"
                >
                  {resending ? t("auth.signup_resending") : t("auth.signup_resend")}
                </Button>
              </div>
            )}
            <div>
              <Label htmlFor="email" className="text-zinc-400 text-xs font-mono uppercase tracking-wider">{t("auth.email_label")}</Label>
              <Input id="email" data-testid="login-email" type="email" required value={email} onChange={(e)=>setEmail(e.target.value)}
                     className="mt-1.5 bg-[#090A0F] border-[#1E212B] focus:border-blue-500 focus-visible:ring-blue-500/30 h-11" />
            </div>
            <div>
              <Label htmlFor="password" className="text-zinc-400 text-xs font-mono uppercase tracking-wider">{t("auth.password_label")}</Label>
              <Input id="password" data-testid="login-password" type="password" required value={password} onChange={(e)=>setPassword(e.target.value)}
                     className="mt-1.5 bg-[#090A0F] border-[#1E212B] focus:border-blue-500 focus-visible:ring-blue-500/30 h-11" />
              <div className="mt-2 text-right">
                <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs text-blue-400 hover:text-blue-300">
                  {t("auth.login_forgot")}
                </Link>
              </div>
            </div>
            <Button type="submit" disabled={busy} data-testid="login-submit" className="w-full bg-blue-600 hover:bg-blue-500 text-white h-11">
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />…</> : t("auth.login_submit")}
            </Button>
          </form>
        </div>
        <p className="text-sm text-zinc-500 text-center mt-6">
          {t("auth.login_no_account")} <Link to="/signup" className="text-blue-400 hover:text-blue-300" data-testid="link-signup">{t("auth.login_no_account_link")}</Link>
        </p>
      </div>
    </div>
  );
}
